// Opens a project source file at the referenced passage. Best-effort: PDFs open
// in the browser viewer (jumping to a page when known); text/markdown render
// inline with the quote highlighted + scrolled into view; images show directly;
// anything else falls back to an "open in new tab" link. Always at least opens
// the file.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProjectFileRead, SourceReference } from "@/api/projects";
import { Spinner } from "@/components/ui";

const TEXT_EXTS = ["txt", "md", "markdown"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp", "gif"];

export function SourceRefModal({
  file,
  refItem,
  onClose,
}: {
  file: ProjectFileRead | null;
  refItem: SourceReference;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const url = file?.download_url ?? null;
  const ext = (file?.file_type || "").toLowerCase();

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-black/10 p-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{refItem.filename}</p>
            {refItem.context && <p className="truncate text-xs text-ink-soft">{refItem.context}</p>}
            {refItem.quote && (
              <p className="mt-0.5 text-xs text-ink-faint">
                {t("Passage")}: “{refItem.quote}”
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="text-xs font-medium text-brand-700 hover:underline">
                {t("Open in new tab")}
              </a>
            )}
            <button type="button" onClick={onClose} className="text-ink-faint hover:text-ink">✕</button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {!url ? (
            <p className="p-6 text-sm text-ink-faint">{t("This source file is unavailable.")}</p>
          ) : ext === "pdf" ? (
            <iframe
              title={refItem.filename}
              src={url + (refItem.page ? `#page=${refItem.page}` : "")}
              className="h-full w-full"
            />
          ) : IMAGE_EXTS.includes(ext) ? (
            <div className="flex h-full items-center justify-center overflow-auto p-4">
              <img src={url} alt={refItem.filename} className="max-h-full max-w-full" />
            </div>
          ) : TEXT_EXTS.includes(ext) ? (
            <TextPassage url={url} quote={refItem.quote} charOffset={refItem.char_offset} />
          ) : (
            <p className="p-6 text-sm text-ink-soft">
              {t("Preview isn't available for this file type — open it in a new tab.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Fetch a text/markdown file and highlight the referenced passage. */
function TextPassage({ url, quote, charOffset }: { url: string; quote: string; charOffset?: number }) {
  const { t } = useTranslation();
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const markRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(url)
      .then((r) => r.text())
      .then((txt) => alive && setText(txt))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [url]);

  useEffect(() => {
    if (text && markRef.current) markRef.current.scrollIntoView({ block: "center" });
  }, [text]);

  if (failed) {
    return <p className="p-6 text-sm text-ink-soft">{t("Couldn't load the file text — open it in a new tab.")}</p>;
  }
  if (text === null) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  }

  // Locate the quote (prefer char_offset; else search). Split into before/match/after.
  const needle = (quote || "").slice(0, 80);
  let idx = -1;
  if (typeof charOffset === "number" && charOffset >= 0 && charOffset < text.length) {
    idx = charOffset;
  } else if (needle) {
    idx = text.indexOf(needle);
  }
  const end = idx >= 0 ? idx + (needle.length || 0) : -1;

  return (
    <div className="h-full overflow-auto whitespace-pre-wrap break-words p-4 text-sm leading-relaxed text-ink">
      {idx >= 0 ? (
        <>
          {text.slice(0, idx)}
          <mark ref={markRef} className="rounded bg-amber-200 px-0.5">
            {text.slice(idx, end > idx ? end : idx + Math.max(needle.length, 1))}
          </mark>
          {text.slice(end > idx ? end : idx + Math.max(needle.length, 1))}
        </>
      ) : (
        text
      )}
    </div>
  );
}
